import React, { useState } from 'react';

import { CopyOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Drawer, Collapse, Typography, Tag, Space, Input, Button, message } from 'antd';
import { useTranslation } from 'react-i18next';

import { LIQUID_VARIABLES_DOC } from '@/constants/liquidTemplateData';

const { Panel } = Collapse;
const { Title, Text, Paragraph } = Typography;
const { Search } = Input;

/**
 * LiquidVariablesDrawer - Drawer component to display available Liquid variables
 *
 * @param {Object} props
 * @param {boolean} props.visible - Whether drawer is visible
 * @param {Function} props.onClose - Close handler
 */
export default function LiquidVariablesDrawer({ visible, onClose }) {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');

  // 複製到剪貼簿
  const copyToClipboard = async (text, description) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(t('shared.order_templates.variables.copied', { field: description }));
    } catch (err) {
      message.error(t('common.error.copy_failed'));
    }
  };

  // 過濾變數
  const filterVariables = (variables) => {
    if (!searchText) return variables;

    const lowerSearch = searchText.toLowerCase();
    return Object.entries(variables).reduce((acc, [key, value]) => {
      if (
        key.toLowerCase().includes(lowerSearch) ||
        value.description?.toLowerCase().includes(lowerSearch) ||
        value.example?.toString().toLowerCase().includes(lowerSearch)
      ) {
        acc[key] = value;
      }
      return acc;
    }, {});
  };

  // 渲染變數欄位
  const renderField = (fieldName, fieldInfo, parentPath = '') => {
    // fieldInfo.variable：明確指定變數路徑，覆寫「群組前綴.欄位名」的預設組法。
    // 用於某欄位歸在某群組顯示、但實際變數在別的物件下（如 訂單基本資訊 群組裡的
    // 訂單編號其實取 fulfillment.name）。
    const fullPath = fieldInfo.variable || (parentPath ? `${parentPath}.${fieldName}` : fieldName);
    const liquidSyntax = `{{ ${fullPath} }}`;

    return (
      <div
        key={fullPath}
        style={{
          marginBottom: '16px',
          padding: '12px',
          backgroundColor: '#fafafa',
          borderRadius: '4px',
          border: '1px solid #f0f0f0'
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          {/* 欄位名稱 */}
          <Space>
            <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
              {fieldName}
            </Text>
            <Tag color="blue">{fieldInfo.type}</Tag>
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyToClipboard(liquidSyntax, fieldInfo.description)}
            />
          </Space>

          {/* 說明 */}
          <Text type="secondary">{fieldInfo.description}</Text>

          {/* Liquid 語法 */}
          <div>
            <Text code copyable={{ text: liquidSyntax }}>
              {liquidSyntax}
            </Text>
          </div>

          {/* 範例值 */}
          {fieldInfo.example && (
            <div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {t('shared.order_templates.variables.example')}:{' '}
              </Text>
              <Tag color="green">{String(fieldInfo.example)}</Tag>
            </div>
          )}

          {/* 格式說明（日期等） */}
          {fieldInfo.format && (
            <div>
              <Text type="warning" style={{ fontSize: '12px' }}>
                <InfoCircleOutlined /> {fieldInfo.format}
              </Text>
            </div>
          )}

          {/* 備註 */}
          {fieldInfo.note && (
            <div>
              <Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic' }}>
                {fieldInfo.note}
              </Text>
            </div>
          )}
        </Space>
      </div>
    );
  };

  // 渲染 Filter 項目
  const renderFilter = (filterData) => {
    return (
      <div
        key={filterData.name}
        style={{
          marginBottom: '24px',
          padding: '16px',
          backgroundColor: '#f6ffed',
          borderRadius: '4px',
          border: '1px solid #b7eb8f'
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {/* Filter 名稱 */}
          <div>
            <Space>
              <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                {filterData.name}
              </Text>
              <Tag color="green">Filter</Tag>
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(filterData.syntax, filterData.name)}
              />
            </Space>
          </div>

          {/* 說明 */}
          <Text type="secondary">{filterData.description}</Text>

          {/* 語法 */}
          <div>
            <Text strong>語法：</Text>
            <br />
            <Text code copyable={{ text: filterData.syntax }}>
              {filterData.syntax}
            </Text>
          </div>

          {/* 參數 */}
          {filterData.parameters && filterData.parameters.length > 0 && (
            <div>
              <Text strong>參數：</Text>
              {filterData.parameters.map((param, idx) => (
                <div key={idx} style={{ marginLeft: '16px', marginTop: '8px' }}>
                  <Space>
                    <Tag color={param.optional ? 'default' : 'blue'}>
                      {param.name}
                    </Tag>
                    <Text type="secondary">
                      {param.type}
                      {param.optional && ' (可選)'}
                      {param.default !== undefined && ` - 預設: ${param.default}`}
                    </Text>
                  </Space>
                  <div style={{ marginLeft: '24px', marginTop: '4px' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {param.description}
                    </Text>
                  </div>
                  {param.options && (
                    <div style={{ marginLeft: '24px', marginTop: '4px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        選項：{param.options.join(', ')}
                      </Text>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* 範例 */}
          {filterData.examples && filterData.examples.length > 0 && (
            <div>
              <Text strong>範例：</Text>
              {filterData.examples.map((example, idx) => (
                <div key={idx} style={{ marginTop: '8px' }}>
                  <Text code copyable={{ text: example.code }}>
                    {example.code}
                  </Text>
                  <div style={{ marginTop: '4px' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {example.description}
                    </Text>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 備註 */}
          {filterData.notes && filterData.notes.length > 0 && (
            <div>
              <Text type="warning" style={{ fontSize: '12px' }}>
                <InfoCircleOutlined /> 備註：
              </Text>
              <ul style={{ marginTop: '4px', marginBottom: 0, paddingLeft: '20px' }}>
                {filterData.notes.map((note, idx) => (
                  <li key={idx}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {note}
                    </Text>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Space>
      </div>
    );
  };

  // 渲染變數分類
  const renderCategory = (categoryKey, categoryData) => {
    // Special handling for filters category
    if (categoryKey === 'filters') {
      const items = categoryData.items || [];

      if (items.length === 0) {
        return null;
      }

      return (
        <Panel
          header={
            <Space>
              <Text strong>{t(`shared.order_templates.variables.categories.${categoryKey}`)}</Text>
              <Tag color="green">{items.length} Filters</Tag>
            </Space>
          }
          key={categoryKey}
        >
          {/* 分類說明 */}
          <Paragraph type="secondary" style={{ marginBottom: '16px' }}>
            {categoryData.description}
          </Paragraph>

          {/* 額外說明 */}
          {categoryData.note && (
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fffbe6', borderRadius: '4px' }}>
              <Text type="warning">
                <InfoCircleOutlined /> {categoryData.note}
              </Text>
            </div>
          )}

          {/* Filter 列表 */}
          {items.map(filterData => renderFilter(filterData))}
        </Panel>
      );
    }

    // Normal category rendering
    const filteredFields = filterVariables(categoryData.fields || {});

    if (Object.keys(filteredFields).length === 0 && searchText) {
      return null; // 搜尋時若無結果則不顯示此分類
    }

    return (
      <Panel
        header={
          <Space>
            <Text strong>{t(`shared.order_templates.variables.categories.${categoryKey}`)}</Text>
            <Tag>{Object.keys(filteredFields).length} {t('shared.order_templates.variables.fields')}</Tag>
          </Space>
        }
        key={categoryKey}
      >
        {/* 分類說明 */}
        <Paragraph type="secondary" style={{ marginBottom: '16px' }}>
          {categoryData.description}
        </Paragraph>

        {/* 類型標記（陣列） */}
        {categoryData.type === 'Array' && (
          <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#e6f7ff', borderRadius: '4px' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>
                <InfoCircleOutlined /> {t('shared.order_templates.variables.array_type')}
              </Text>
              {categoryData.loop_example && (
                <Text code copyable={{ text: categoryData.loop_example }}>
                  {categoryData.loop_example}
                </Text>
              )}
              {categoryData.condition && (
                <div>
                  <Text type="secondary">{t('shared.order_templates.variables.condition')}:</Text>
                  <br />
                  <Text code copyable={{ text: categoryData.condition }}>
                    {categoryData.condition}
                  </Text>
                </div>
              )}
            </Space>
          </div>
        )}

        {/* 欄位列表 */}
        {Object.entries(filteredFields).map(([fieldName, fieldInfo]) =>
          renderField(fieldName, fieldInfo, categoryKey === 'helpers' ? '' : categoryKey)
        )}
      </Panel>
    );
  };

  return (
    <Drawer
      title={t('shared.order_templates.variables.title')}
      placement="right"
      width={600}
      onClose={onClose}
      open={visible}
      styles={{ body: { paddingBottom: 80 } }}
    >
      {/* 說明 */}
      <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f0f5ff', borderRadius: '4px' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Title level={5}>
            <InfoCircleOutlined /> {t('shared.order_templates.variables.guide.title')}
          </Title>
          <Paragraph style={{ marginBottom: 0 }}>
            {t('shared.order_templates.variables.guide.description')}
          </Paragraph>
          <div>
            <Text strong>{t('shared.order_templates.variables.guide.basic_syntax')}:</Text>
            <ul style={{ marginTop: '8px', marginBottom: 0 }}>
              <li>
                <Text code>{'{{ variable }}'}</Text> - {t('shared.order_templates.variables.guide.output')}
              </li>
              <li>
                <Text code>{'{% if condition %}...{% endif %}'}</Text> - {t('shared.order_templates.variables.guide.condition')}
              </li>
              <li>
                <Text code>{'{% for item in items %}...{% endfor %}'}</Text> - {t('shared.order_templates.variables.guide.loop')}
              </li>
              <li>
                <Text code>{'{{ date | date: "%Y-%m-%d" }}'}</Text> - {t('shared.order_templates.variables.guide.filter')}
              </li>
            </ul>
          </div>
        </Space>
      </div>

      {/* 搜尋 */}
      <Search
        placeholder={t('shared.order_templates.variables.search_placeholder')}
        allowClear
        onChange={(e) => setSearchText(e.target.value)}
        style={{ marginBottom: '16px' }}
      />

      {/* 變數分類 */}
      <Collapse defaultActiveKey={['order', 'items']} ghost>
        {Object.entries(LIQUID_VARIABLES_DOC).map(([categoryKey, categoryData]) =>
          renderCategory(categoryKey, categoryData)
        )}
      </Collapse>

      {/* 底部提示 */}
      <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#fffbe6', borderRadius: '4px' }}>
        <Text type="warning">
          <InfoCircleOutlined /> {t('shared.order_templates.variables.tip')}
        </Text>
      </div>
    </Drawer>
  );
}
